import React, { useState, useRef, useEffect, useCallback } from 'react';
import { enviarMensaje, leerArchivoBase64, clasificarArchivo, leerProyectosSheets, guardarEntregableEnSheets } from '../services/claudeApi';

const SUGERENCIAS = [
  '¿Cuál es la dotación de diseño para edificio residencial según NEC-HS?',
  '¿Qué períodos de retorno usar para drenaje pluvial urbano?',
  '¿Cómo se calcula el volumen de cisterna según NEC-HS?',
  '¿Qué parámetros morfométricos necesito para modelación HEC-RAS?',
  '¿Cuál es el procedimiento para calibrar un modelo WaterGEMS?',
  '¿Qué documentos entrega ARCSEG en un diseño hidro-sanitario BIM?',
  'Genera una memoria de cálculo hidráulico para red AP de edificio 8 pisos',
  '¿Cuál es la velocidad mínima en tuberías de alcantarillado?',
];

const TIPOS_ARCHIVO_ACEPTADOS = '.pdf,.png,.jpg,.jpeg,.xlsx,.xls,.dwg,.csv';

function FilePreview({ archivos, onRemove }) {
  if (!archivos.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0' }}>
      {archivos.map((a, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#F0F4F8', border: '1px solid #D6E0EC',
          borderRadius: 8, padding: '4px 10px', fontSize: 12
        }}>
          <span>{a.icono}</span>
          <span style={{ color: '#1A2E44', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.nombre}
          </span>
          <button onClick={() => onRemove(i)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94A3B8', fontSize: 14, padding: 0, lineHeight: 1
          }}>×</button>
        </div>
      ))}
    </div>
  );
}

function Mensaje({ msg }) {
  const esAsistente = msg.role === 'assistant';

  const renderContenido = (texto) => {
    return texto
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code style="background:#F0F4F8;padding:1px 5px;border-radius:3px;font-family:monospace;font-size:12px">$1</code>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div style={{
      display: 'flex', gap: 10, marginBottom: 16,
      flexDirection: esAsistente ? 'row' : 'row-reverse'
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: esAsistente ? '#1A2E44' : '#E8863A',
        fontSize: 14
      }}>
        {esAsistente ? '🧠' : '👤'}
      </div>
      <div style={{ maxWidth: '80%' }}>
        {/* Archivos adjuntos del mensaje */}
        {msg.archivos && msg.archivos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {msg.archivos.map((a, i) => (
              <span key={i} style={{
                background: '#E8F0FE', border: '1px solid #C5D8F6',
                borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#1A2E44'
              }}>
                {a.icono} {a.nombre}
              </span>
            ))}
          </div>
        )}
        <div style={{
          background: esAsistente ? '#F8FAFC' : '#E8863A',
          color: esAsistente ? '#1A2E44' : '#fff',
          borderRadius: esAsistente ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
          padding: '10px 14px',
          fontSize: 14, lineHeight: 1.6,
          border: esAsistente ? '1px solid #E8ECF0' : 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
        }}>
          <div dangerouslySetInnerHTML={{ __html: renderContenido(msg.content) }} />
        </div>
        <div style={{ fontSize: 11, color: '#A8B5C2', marginTop: 4, textAlign: esAsistente ? 'left' : 'right' }}>
          {msg.hora}
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: '#1A2E44', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
      }}>🧠</div>
      <div style={{
        background: '#F8FAFC', border: '1px solid #E8ECF0',
        borderRadius: '4px 16px 16px 16px', padding: '12px 16px',
        display: 'flex', gap: 4, alignItems: 'center'
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: '50%', background: '#94A3B8',
            animation: 'pulse 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`
          }} />
        ))}
      </div>
    </div>
  );
}

const ahora = () => new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

const ICONO_ARCHIVO = (tipo) => {
  if (tipo.startsWith('image/')) return '🖼️';
  if (tipo === 'application/pdf') return '📄';
  if (tipo.includes('excel') || tipo.includes('spreadsheet') || tipo.includes('xls')) return '📊';
  return '📎';
};

export default function CerebroIA() {
  const [historial, setHistorial] = useState([{
    role: 'assistant',
    content: `Hola, soy el **Cerebro IA de ARCSEG**. Estoy aquí para apoyarte en:

• 💧 **Hidráulica** — redes AP/AS, cálculos de presión, modelación WaterGEMS
• 🌊 **Hidrología** — cuencas, caudales de diseño, HEC-RAS, curvas IDF
• 🏗️ **Hidro-sanitaria** — instalaciones internas, BIM MEP, NEC-HS
• 🗺️ **SIG** — QGIS, cartografía, análisis geoespacial
• 📐 **BIM** — Revit MEP, Navisworks, clash detection
• 📋 **Memorias y presupuestos** — bajo normativa ecuatoriana

Puedes **escribir libremente**, **adjuntar planos, PDFs o Excel** y pedirme lo que necesites. ¿En qué empezamos?`,
    hora: ahora()
  }]);
  const [input, setInput] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSel, setProyectoSel] = useState('');
  const [mostrarSugerencias, setMostrarSugerencias] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [tab, setTab] = useState('chat');

  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    leerProyectosSheets().then(d => setProyectos(d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [historial, cargando]);

  const agregarArchivos = async (files) => {
    const nuevos = [];
    for (const file of Array.from(files)) {
      try {
        const data = await leerArchivoBase64(file);
        const { tipo, mediaType } = clasificarArchivo(file);
        nuevos.push({
          nombre: file.nombre || file.name,
          data,
          tipo,
          mediaType,
          icono: ICONO_ARCHIVO(file.type),
          size: file.size
        });
      } catch (e) {
        console.error('Error leyendo archivo:', e);
      }
    }
    setArchivos(prev => [...prev, ...nuevos]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    agregarArchivos(e.dataTransfer.files);
  }, []);

  const handleDragOver = (e) => e.preventDefault();

  const enviar = async (textoDirecto) => {
    const texto = textoDirecto || input.trim();
    if ((!texto && archivos.length === 0) || cargando) return;

    const archivosCopia = [...archivos];
    setInput('');
    setArchivos([]);
    setMostrarSugerencias(false);
    setCargando(true);

    const mensajeUsuario = {
      role: 'user',
      content: texto || (archivosCopia.length > 0 ? 'Analiza los archivos adjuntos.' : ''),
      archivos: archivosCopia.map(a => ({ nombre: a.nombre, icono: a.icono })),
      hora: ahora()
    };

    const nuevoHistorial = [...historial, mensajeUsuario];
    setHistorial(nuevoHistorial);

    try {
      // Construir historial limpio para la API (solo role y content)
      const historialApi = nuevoHistorial.map(m => ({
        role: m.role,
        content: m.content
      }));

      const respuesta = await enviarMensaje(historialApi, archivosCopia);
      setHistorial(prev => [...prev, {
        role: 'assistant',
        content: respuesta,
        hora: ahora()
      }]);
    } catch (err) {
      setHistorial(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ ${err.message}`,
        hora: ahora()
      }]);
    }
    setCargando(false);
    inputRef.current?.focus();
  };

  const guardarConversacion = async () => {
    if (!proyectoSel) return;
    setGuardando(true);
    try {
      const contenido = historial
        .filter(m => m.role !== 'assistant' || historial.indexOf(m) > 0)
        .map(m => `[${m.role.toUpperCase()} ${m.hora}]\n${m.content}`)
        .join('\n\n---\n\n');
      await guardarEntregableEnSheets({
        codigoProyecto: proyectoSel,
        nombreDocumento: `Sesión Cerebro IA — ${new Date().toLocaleDateString('es-EC')}`,
        tipoDocumento: 'Consulta_IA',
        contenido,
        responsable: 'Cerebro IA — ARCSEG'
      });
      alert('✅ Conversación guardada en Drive y Sheets.');
    } catch (e) {
      alert('Error al guardar: ' + e.message);
    }
    setGuardando(false);
  };

  const limpiarChat = () => {
    setHistorial([{
      role: 'assistant',
      content: 'Chat limpiado. ¿En qué puedo ayudarte?',
      hora: ahora()
    }]);
    setMostrarSugerencias(true);
  };

  const copiarUltimaRespuesta = () => {
    const ultima = [...historial].reverse().find(m => m.role === 'assistant');
    if (ultima) navigator.clipboard.writeText(ultima.content);
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        .send-btn:hover { background: #c4722e !important; }
        .send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .attach-btn:hover { background: #E8ECF0 !important; }
        .sugerencia-btn:hover { background: #E8F0FE !important; border-color: #93B8E8 !important; color: #1A2E44 !important; }
        .action-btn:hover { background: #E8ECF0 !important; }
        textarea:focus { outline: none; box-shadow: 0 0 0 2px rgba(232,134,58,0.2); border-color: #E8863A !important; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🧠</span>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1A2E44', margin: 0 }}>Cerebro IA</h1>
            <p style={{ fontSize: 12, color: '#6B7A89', margin: 0 }}>Asistente técnico ARCSEG · Powered by Claude</p>
          </div>
          <span style={{ fontSize: 11, background: '#DCFCE7', color: '#15803D', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>● Activo</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            value={proyectoSel}
            onChange={e => setProyectoSel(e.target.value)}
            style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 8,
              border: '1px solid #D6E0EC', background: '#fff', color: '#1A2E44',
              cursor: 'pointer'
            }}
          >
            <option value="">Sin proyecto activo</option>
            {proyectos.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo} — {p.nombre}</option>)}
          </select>
          {proyectoSel && (
            <button onClick={guardarConversacion} disabled={guardando} className="action-btn" style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 8,
              border: '1px solid #D6E0EC', background: '#fff', color: '#1A2E44', cursor: 'pointer'
            }}>
              {guardando ? '⏳' : '💾'} Guardar
            </button>
          )}
          <button onClick={copiarUltimaRespuesta} className="action-btn" style={{
            fontSize: 12, padding: '4px 10px', borderRadius: 8,
            border: '1px solid #D6E0EC', background: '#fff', color: '#1A2E44', cursor: 'pointer'
          }}>
            📋 Copiar
          </button>
          <button onClick={limpiarChat} className="action-btn" style={{
            fontSize: 12, padding: '4px 10px', borderRadius: 8,
            border: '1px solid #D6E0EC', background: '#fff', color: '#6B7A89', cursor: 'pointer'
          }}>
            🗑️ Limpiar
          </button>
        </div>
      </div>

      {/* Área del chat */}
      <div
        ref={chatRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          flex: 1, overflowY: 'auto', padding: '16px',
          background: '#fff', border: '1px solid #E8ECF0',
          borderRadius: '12px 12px 0 0',
          scrollBehavior: 'smooth'
        }}
      >
        {historial.map((msg, i) => <Mensaje key={i} msg={msg} />)}
        {cargando && <TypingIndicator />}

        {/* Sugerencias iniciales */}
        {mostrarSugerencias && historial.length === 1 && (
          <div style={{ marginTop: 8 }}>
            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>Consultas frecuentes:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUGERENCIAS.map((s, i) => (
                <button key={i} onClick={() => enviar(s)} className="sugerencia-btn" style={{
                  background: '#F8FAFC', border: '1px solid #E8ECF0',
                  borderRadius: 20, padding: '5px 12px', fontSize: 12,
                  color: '#6B7A89', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit', transition: 'all 0.15s'
                }}>
                  {s.length > 55 ? s.substring(0, 55) + '...' : s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Zona de input */}
      <div style={{
        background: '#fff', border: '1px solid #E8ECF0', borderTop: 'none',
        borderRadius: '0 0 12px 12px', padding: '10px 14px', flexShrink: 0
      }}>
        {/* Preview archivos adjuntos */}
        <FilePreview archivos={archivos} onRemove={(i) => setArchivos(prev => prev.filter((_, idx) => idx !== i))} />

        {/* Arrastrar archivos hint */}
        {archivos.length === 0 && (
          <div style={{ fontSize: 11, color: '#CBD5E1', marginBottom: 6 }}>
            💡 Puedes arrastrar planos, PDFs o Excel directamente aquí
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {/* Botón adjuntar */}
          <button
            className="attach-btn"
            onClick={() => fileRef.current?.click()}
            title="Adjuntar archivo"
            style={{
              width: 38, height: 38, borderRadius: 8, border: '1px solid #D6E0EC',
              background: '#F8FAFC', cursor: 'pointer', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.15s'
            }}
          >
            📎
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={TIPOS_ARCHIVO_ACEPTADOS}
            style={{ display: 'none' }}
            onChange={e => agregarArchivos(e.target.files)}
          />

          {/* Textarea */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            placeholder="Escribe tu consulta técnica... (Enter para enviar, Shift+Enter para nueva línea)"
            rows={1}
            style={{
              flex: 1, resize: 'none', border: '1px solid #D6E0EC',
              borderRadius: 8, padding: '8px 12px', fontSize: 14,
              fontFamily: 'inherit', lineHeight: 1.5, color: '#1A2E44',
              background: '#fff', minHeight: 38, maxHeight: 120,
              overflow: 'auto', transition: 'border-color 0.15s, box-shadow 0.15s'
            }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />

          {/* Botón enviar */}
          <button
            className="send-btn"
            onClick={() => enviar()}
            disabled={cargando || (!input.trim() && archivos.length === 0)}
            style={{
              width: 38, height: 38, borderRadius: 8, border: 'none',
              background: '#E8863A', color: '#fff', cursor: 'pointer',
              fontSize: 16, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
              transition: 'background 0.15s'
            }}
          >
            {cargando ? '⏳' : '➤'}
          </button>
        </div>

        <div style={{ marginTop: 6, fontSize: 11, color: '#CBD5E1', textAlign: 'center' }}>
          Acepta PDF · Excel · Imágenes · Planos · DWG · CSV · Arrastra o usa 📎
        </div>
      </div>
    </div>
  );
}